<template>
  <div>
    <div class="form-header text-center pt-2 pb-3">
      <h3>{{ pageTitle }}</h3>
      <p>{{ pageDescription }}</p>
    </div>

    <div
      class="form mb-5"
      v-if="hasValidBusiness"
    >
      <div
        :key="index"
        v-for="(item, index) in businesses"
      >
        <div
          class="row business-card border rounded p-2 mb-2 shadow-sm"
          v-if="item.business.name"
        >
          <div class="col">
            <div class="name mb-1">
              <div class="business_card__header">
                <small class="business-card__title mr-1">Business Details</small>
                <router-link
                  :to="`/businesses/${index}/business-details?from=overview`"
                  class="small"
                >
                  <img
                    alt="Edit Business Details"
                    src="@/assets/images/edit-icon.svg"
                    title="Edit Business Details"
                  >
                </router-link>
              </div>
            </div>
            <div v-if="hasValidBusiness">
              <div class="mb-1">{{ item.business.name }}</div>
              {{item.business.profileAttributes.address1}}
              <br>
              <template v-show="item.business.profileAttributes.address2">
                {{item.business.profileAttributes.address2}}
                <br>
              </template>
              {{item.business.profileAttributes.city}},
              {{item.business.profileAttributes.state}}
              {{item.business.profileAttributes.zip}}
            </div>
            <div v-else>
              <div class="btn btn-generic-secondary btn-sm">Add Business Details</div>
            </div>
          </div>
          <div class="col col-2">
            <div>
              <div class="mb-1">
                <div class="business_card__header">
                  <small class="business-card__title mr-1">Plan</small>
                  <router-link
                    :to="`/businesses/${index}/plan-selection?from=overview`"
                    class="small"
                  >
                    <img
                      alt="Edit Plan"
                      src="@/assets/images/edit-icon.svg"
                      title="Edit Plan"
                    >
                  </router-link>
                </div>
              </div>
              <div>{{item.subscription.planName}}</div>
            </div>
          </div>
          <div class="col">
            <div>
              <div class="business_card__header mb-1">
                <small class="business-card__title mr-1">Payment Method</small>
                <router-link
                  :to="`/businesses/${index}/billing-details?from=overview`"
                  class="small"
                  v-if="hasPaymentMethod(index)"
                >
                  <img
                    alt="Edit Payment Method"
                    src="@/assets/images/edit-icon.svg"
                    title="Edit Payment Method"
                  >
                </router-link>
              </div>
              <div>
                <div v-if="hasPaymentMethod(index)">
                  <span>{{ getPaymentMethodType(index) }}</span>
                  ending in {{ getPaymentMethodLastFour(index) }}
                </div>
                <div v-else>
                  <router-link
                    :to="`/businesses/${index}/billing-details?from=overview`"
                    class="btn btn-generic-secondary btn-sm"
                  >Add Payment Method</router-link>
                </div>
              </div>
            </div>
          </div>
          <div class="col-auto">
            <img
              @click="deleteBusinessFromList(index)"
              alt="Delete Business"
              class="cursor-pointer"
              src="@/assets/images/trash-icon.svg"
              title="Delete Business"
            >
          </div>
        </div>
      </div>
    </div>
    <div>
      <div class="bg-light rounded text-center py-4">
        <button
          @click="addBusiness()"
          class="btn btn-generic-secondary btn-lg"
        >Add Business</button>
      </div>
    </div>
    <div
      class="mt-5"
      v-if="hasValidBusiness"
    >
      <form-nav-buttons
        :goBack="this.$route.meta.goBack"
        :next-route="this.$route.meta.nextRoute"
        :skippable="this.$route.meta.skippable"
      >
        <template
          slot="nextButton"
          slot-scope="next"
        >
          <span
            class="text-danger mr-2"
            v-if="!allBusinessesHavePaymentMethods"
          >To complete signup you must add payment methods to all businesses.</span>
          <span
            class="text-success mr-2"
            v-else
          >All businesses have payment methods.</span>
          <button
            :disabled="!allBusinessesHavePaymentMethods"
            @click="completeSignup(next.next)"
            class="btn btn-brand-primary btn-lg"
            type="button"
          >Complete Signup</button>
        </template>
      </form-nav-buttons>
    </div>
  </div>
</template>

<script>
import { FormNavButtons } from 'components';
import API from '@/api';
import { mapActions, mapState } from 'vuex';
import SESSION from '@/session';

export default {
  components: {
    FormNavButtons,
  },
  computed: {
    ...mapState({
      businessLength: state => state.businessInfo.businesses.length,
    }),
    hasValidBusiness() {
      return (this.businesses.length) ? this.businesses[0].business.name !== '' : false;
    },
    businesses() {
      return this.$store.getters.getBusinesses;
    },
    paymentMethods() {
      return this.$store.getters.getPaymentMethods;
    },
    allBusinessesHavePaymentMethods() {
      return (this.businesses.length) ?
        this.businesses.every(element => element.paymentMethod.stripeCardToken !== '') :
        false;
    },
    isLoggedIn() {
      return SESSION.isOwnerLoggedIn();
    },
    pageTitle() {
      return (this.hasValidBusiness) ? this.$route.meta.name : 'You don\'t have any business.';
    },
    pageDescription() {
      return (this.hasValidBusiness) ? this.$route.meta.description : 'Please add a business to continue.';
    },
  },
  methods: {
    ...mapActions([
      'createOwner',
      'setRequestAuthenticationToken',
      'setSessionStorageAccessToken',
      'setSessionStorageRefreshToken',
      'saveOnboardingState',
    ]),
    addNewPaymentMethod(index) {
      this.$router.push(`/businesses/${index}/billing-details?from=overview`);
    },
    hasPaymentMethod(index) {
      return this.businesses[index].paymentMethod.stripeCardToken !== '';
    },
    getPaymentMethod(index) {
      const paymentMethodToken = this.businesses[index].paymentMethod.stripeCardToken;
      const paymentMethodsWithoutUndefined = this.paymentMethods.filter(e => e !== undefined);
      const paymentMethod =
        paymentMethodsWithoutUndefined.find(item => item.tokens.includes(paymentMethodToken));
      return paymentMethod;
    },
    getPaymentMethodType(index) {
      let cardType = '';
      const paymentMethod = this.getPaymentMethod(index);
      if (paymentMethod) {
        cardType = paymentMethod.type;
      }
      return cardType;
    },
    getPaymentMethodLastFour(index) {
      let lastFourDigit = '';
      const paymentMethod = this.getPaymentMethod(index);
      if (paymentMethod) {
        lastFourDigit = paymentMethod.lastFour;
      }
      return lastFourDigit;
    },
    addBusiness() {
      this.$store.dispatch('createEmptyBusiness').then(() => {
        if (this.hasValidBusiness) {
          this.$router.push(`/businesses/${this.businessLength - 1}/business-details`);
        } else {
          this.$router.push('/businesses/0/business-details');
        }
      });
    },
    deleteBusinessFromList(index) {
      const accepted = confirm('Do you really want to delete the business?'); // eslint-disable-line no-alert
      if (accepted) {
        this.businesses.splice(index, 1);
        this.$toast.open({
          message: 'Business deleted successfully.',
          type: 'is-success',
        });
        this.$store.dispatch('setBusinessAfterDeletion', this.businesses);
        this.saveOnboardingState();
      }
    },
    completeSignup() {
      if (this.isLoggedIn) {
        this.submitToLMA();
      } else {
        this.createOwner().then((response) => {
          this.setSessionTokens(response.data);
          this.submitToLMA();
        });
      }
    },
    setSessionTokens(tokens) {
      this.setSessionStorageAccessToken(tokens.access_token);
      this.setSessionStorageRefreshToken(tokens.refresh_token);
      this.setRequestAuthenticationToken();
    },
    submitToLMA() {
      const LMAPayload = this.$store.state.businessInfo;

      return API.createAccount(LMAPayload)
        .then((response) => {
          this.handleSuccess(response);
        })
        .catch((error) => {
          this.handleError(error.response.data.error);
        });
    },
    handleSuccess(response) {
      this.status = 'complete';
      this.$router.push({
        path: '/success',
        query: { login_link: response.data.redirect_to },
      });
    },
    handleError(message) {
      this.$toast.open({
        message,
        type: 'is-danger',
      });
    },
  },
};
</script>

<style lang="scss">
.business-card__title {
  font-weight: bold;
}
.cursor-pointer {
  cursor: pointer;
}
</style>
